/**
 * PURPOSE: Contains logic related to the vAuto.
 */

// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { DashMiddleware, DashNext, DashStore } from '@makemydeal/dr-shared-store';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import { bridgeUserActionTypes } from '@makemydeal/dr-shared-store';
import { MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';

// consts/enums
import { VAUTO_APPRAISALS_SUCCESS } from '../actionTypes/vAutoActionTypes';

// actions
import { getVAutoAppraisals } from '../actions/vAutoActionCreators';
import { mapTradeInsToAppraisals, shouldOverrideDealAcvWithVAutoAppraisal } from '../utils/vAutoUtils';
import type { VAutoItem } from '@makemydeal/dr-dash-types';
import * as tradeInsActionCreators from '../actions/tradeInsActionCreators';

// selectors
import { getOfferCreationDateString } from '../selectors/offerRedux';
import * as tradeInsSelectors from '../selectors/tradeIns';

export const middleware: DashMiddleware = (store: DashStore) => (next: DashNext) => (action: AnyFSA) => {
    if (action.type === bridgeUserActionTypes.BRIDGE_USER_SUCCESS) {
        const platformUserId = action.payload.id;

        store.dispatch(getVAutoAppraisals(MANAGER_VIEW_APP_PREFIX, platformUserId));
    }

    if (action.type === VAUTO_APPRAISALS_SUCCESS) {
        // An empty 200/204 leaves `payload` undefined. This runs before `next(action)`, so throwing
        // here kept the action from reaching the reducer and left every ACV field stuck loading.
        const vAutoAppraisal: VAutoItem[] = action.payload?.data?.items ?? [];
        if (!vAutoAppraisal.length) {
            return next(action);
        }

        const state = store.getState();
        const isTradeAcvOverrideEnabled = dealerSelectors.isTradeAcvOverrideEnabled(state);
        const offerCreatedOn = getOfferCreationDateString(state);

        const multiTradeInAppraisals = mapTradeInsToAppraisals(state, vAutoAppraisal);
        multiTradeInAppraisals.forEach(({ appraisal, index, vin }) => {
            if (vin && appraisal && appraisal.value > 0) {
                const dealAcv = tradeInsSelectors.getTradeACV(index)(state);
                const shouldOverrideAcv = shouldOverrideDealAcvWithVAutoAppraisal(
                    isTradeAcvOverrideEnabled,
                    offerCreatedOn,
                    appraisal.updatedOn,
                    appraisal.value,
                    dealAcv
                );

                if (shouldOverrideAcv) {
                    store.dispatch(tradeInsActionCreators.acvChanged(index)(appraisal.value));
                }
            }
        });
    }

    return next(action);
};
